/* impressiko-price-bar.liquid */
(() => {
    if (customElements.get('impressiko-price-bar')) return;

    class ImpressikoPriceBar extends HTMLElement {
      connectedCallback() {
        this.variantData = this.querySelector('[data-ipb-variants]');
        this.priceElement = this.querySelector('[data-ipb-price]');
        this.compareElement = this.querySelector('[data-ipb-compare]');
        this.discountElement = this.querySelector('[data-ipb-discount]');
        this.discountTextElement = this.querySelector('[data-ipb-discount-text]');
        this.productInfo = this.closest('product-info');
        this.sectionId = this.dataset.sectionId;
        this.discountSuffix = this.dataset.discountSuffix || '';
        this.variants = [];
        this.syncTimers = [];
        this.currentVariantId = null;

        try {
          this.variants = JSON.parse(this.variantData?.textContent || '[]');
        } catch (error) {
          this.variants = [];
        }

        this.onChange = this.handleChange.bind(this);
        this.onVariantEvent = this.handleVariantEvent.bind(this);
        this.onPopstate = this.scheduleSync.bind(this);

        document.addEventListener('change', this.onChange, true);
        document.addEventListener('variant:change', this.onVariantEvent, true);
        document.addEventListener('variantChange', this.onVariantEvent, true);
        document.addEventListener('variant-change', this.onVariantEvent, true);
        document.addEventListener('product:variant-change', this.onVariantEvent, true);
        window.addEventListener('popstate', this.onPopstate);

        this.observeProductInfo();
        this.scheduleSync();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onChange, true);
        document.removeEventListener('variant:change', this.onVariantEvent, true);
        document.removeEventListener('variantChange', this.onVariantEvent, true);
        document.removeEventListener('variant-change', this.onVariantEvent, true);
        document.removeEventListener('product:variant-change', this.onVariantEvent, true);
        window.removeEventListener('popstate', this.onPopstate);
        this.observer?.disconnect();
        this.syncTimers.forEach((timer) => window.clearTimeout(timer));
      }

      observeProductInfo() {
        if (!this.productInfo) return;
        this.observer = new MutationObserver(() => this.scheduleSync());
        this.observer.observe(this.productInfo, {
          subtree: true,
          attributes: true,
          attributeFilter: ['value', 'selected', 'checked']
        });
      }

      eventBelongsToThisProduct(event) {
        const eventProductInfo = event.target?.closest?.('product-info');
        if (eventProductInfo && this.productInfo && eventProductInfo !== this.productInfo) return false;

        const detailSection = event.detail?.sectionId || event.detail?.section_id || event.detail?.section;
        if (detailSection && String(detailSection) !== String(this.sectionId)) return false;

        return true;
      }

      handleChange(event) {
        if (!this.eventBelongsToThisProduct(event)) return;
        if (
          event.target?.matches?.('[name="id"], input[type="radio"], select') ||
          event.target?.closest?.('variant-selects, variant-radios')
        ) {
          this.scheduleSync();
        }
      }

      handleVariantEvent(event) {
        if (!this.eventBelongsToThisProduct(event)) return;
        const detail = event.detail || {};
        const directId = detail.variant?.id || detail.productVariant?.id || detail.variantId || detail.variant_id;
        if (directId) this.updateById(directId, true);
        this.scheduleSync();
      }

      scheduleSync() {
        this.syncTimers.forEach((timer) => window.clearTimeout(timer));
        this.syncTimers = [0, 60, 180].map((delay) =>
          window.setTimeout(() => this.syncFromProduct(), delay)
        );
      }

      getVariantId() {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        const formInput = form?.querySelector('[name="id"]');
        if (formInput?.value) return formInput.value;

        const scopedInput = this.productInfo?.querySelector('form[action*="/cart/add"] [name="id"]');
        if (scopedInput?.value) return scopedInput.value;

        const urlId = new URLSearchParams(window.location.search).get('variant');
        if (urlId) return urlId;

        return this.variants[0]?.id || null;
      }

      syncFromProduct() {
        const variantId = this.getVariantId();
        if (variantId) this.updateById(variantId, true);
      }

      updateById(variantId, animate) {
        const variant = this.variants.find((item) => String(item.id) === String(variantId));
        if (!variant) return;

        const changed = String(this.currentVariantId) !== String(variant.id);
        this.currentVariantId = variant.id;
        this.priceElement.textContent = variant.price_money;

        const hasDiscount = Number(variant.compare_at_price) > Number(variant.price);
        this.toggleElement(this.compareElement, hasDiscount);
        this.toggleElement(this.discountElement, hasDiscount);

        if (hasDiscount) {
          this.compareElement.textContent = variant.compare_money;
          if (this.discountTextElement) {
            this.discountTextElement.textContent = `${variant.discount_percentage}${this.discountSuffix}`;
          }
        }

        if (changed && animate && this.dataset.updateAnimation === 'true') {
          this.classList.remove('ipb-is-updating');
          void this.offsetWidth;
          this.classList.add('ipb-is-updating');
          window.setTimeout(() => this.classList.remove('ipb-is-updating'), 460);
        }
      }

      toggleElement(element, show) {
        if (!element) return;
        element.classList.toggle('ipb-is-hidden', !show);
        element.setAttribute('aria-hidden', show ? 'false' : 'true');
      }
    }

    customElements.define('impressiko-price-bar', ImpressikoPriceBar);
  })();

/* impressiko-variant-card-selector.liquid */
(() => {
    if (customElements.get('impressiko-variant-cards')) return;

    class ImpressikoVariantCards extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.productInfo = this.closest('product-info');
        this.track = this.querySelector('[data-ipvs-track]');
        this.cards = Array.from(this.querySelectorAll('[data-ipvs-card]'));
        this.splitOptions = this.dataset.splitOptions === 'true';
        this.sizeTrack = this.querySelector('[data-ipvs-size-track]');
        this.sizeButtons = Array.from(this.querySelectorAll('[data-ipvs-size-button]'));
        this.activeSize = this.dataset.selectedOption1 || '';
        this.previousButton = this.querySelector('[data-ipvs-prev]');
        this.nextButton = this.querySelector('[data-ipvs-next]');
        this.nativePicker = this.querySelector('variant-selects.ipvs__native-picker');
        this.currentVariantId = this.dataset.selectedVariantId;
        this.timers = [];
        this.labelResizeObserver = null;

        try {
          this.variants = JSON.parse(this.querySelector('[data-ipvs-variants]')?.textContent || '[]');
        } catch (error) {
          this.variants = [];
        }

        this.onClick = this.handleClick.bind(this);
        this.onDocumentChange = this.handleDocumentChange.bind(this);
        this.onVariantEvent = this.handleVariantEvent.bind(this);
        this.onScroll = this.updateArrows.bind(this);
        this.onPopstate = this.scheduleExternalSync.bind(this);

        this.addEventListener('click', this.onClick);
        document.addEventListener('change', this.onDocumentChange, true);
        document.addEventListener('variant:change', this.onVariantEvent, true);
        document.addEventListener('variantChange', this.onVariantEvent, true);
        document.addEventListener('product:variant-change', this.onVariantEvent, true);
        this.track?.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        window.addEventListener('popstate', this.onPopstate);

        this.observeVariantInput();
        this.observeAttachedLabels();
        this.setSelected(this.currentVariantId, false);
        requestAnimationFrame(() => {
          this.syncAttachedLabelOffsets();
          this.updateArrows();
        });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('change', this.onDocumentChange, true);
        document.removeEventListener('variant:change', this.onVariantEvent, true);
        document.removeEventListener('variantChange', this.onVariantEvent, true);
        document.removeEventListener('product:variant-change', this.onVariantEvent, true);
        this.track?.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        window.removeEventListener('popstate', this.onPopstate);
        this.variantInputObserver?.disconnect();
        this.labelResizeObserver?.disconnect();
        this.timers.forEach((timer) => clearTimeout(timer));
      }

      observeAttachedLabels() {
        if (this.dataset.layout !== 'vertical_list' || !('ResizeObserver' in window)) return;

        this.labelResizeObserver = new ResizeObserver(() => this.syncAttachedLabelOffsets());
        this.querySelectorAll('.ipvs__attached-top-label, .ipvs__bottom-label--vertical').forEach((label) => {
          this.labelResizeObserver.observe(label);
        });
      }

      syncAttachedLabelOffsets() {
        if (this.dataset.layout !== 'vertical_list') return;

        this.querySelectorAll('[data-ipvs-card-shell]').forEach((shell) => {
          const children = Array.from(shell.children);
          const topLabel = children.find((child) => child.classList.contains('ipvs__attached-top-label'));
          const bottomLabel = children.find((child) => child.classList.contains('ipvs__bottom-label--vertical'));
          const topOverlap = topLabel ? Math.ceil(topLabel.getBoundingClientRect().height / 2) : 0;
          const bottomOverlap = bottomLabel ? Math.ceil(bottomLabel.getBoundingClientRect().height / 2) : 0;

          shell.style.setProperty('--ipvs-shell-top-overlap', `${topOverlap}px`);
          shell.style.setProperty('--ipvs-shell-bottom-overlap', `${bottomOverlap}px`);
        });
      }

      observeVariantInput() {
        const input = this.getProductForm()?.querySelector('[name="id"]');
        if (!input) return;
        this.variantInputObserver = new MutationObserver(() => this.scheduleExternalSync());
        this.variantInputObserver.observe(input, { attributes: true, attributeFilter: ['value'] });
      }

      handleClick(event) {
        const previous = event.target.closest('[data-ipvs-prev]');
        const next = event.target.closest('[data-ipvs-next]');
        if (previous || next) {
          this.scrollTrack(previous ? -1 : 1);
          return;
        }

        const card = event.target.closest('[data-ipvs-card]');
        const sizeButton = event.target.closest('[data-ipvs-size-button]');
        if (sizeButton) {
          if (!sizeButton.disabled) this.selectSize(sizeButton.dataset.optionValue);
          return;
        }

        if (!card || card.disabled) return;
        this.selectVariant(card.dataset.variantId);
      }

      selectSize(optionValue) {
        if (!this.splitOptions || !optionValue) return;

        const currentVariant = this.getVariant(this.currentVariantId);
        const preferredPack = currentVariant?.options?.[1];
        this.setActiveSize(optionValue, true);

        const matchingCards = this.cards.filter((card) =>
          !card.closest('[data-ipvs-card-shell]')?.hidden && !card.disabled
        );
        const preferredAvailable = matchingCards.find((card) => {
          const variant = this.getVariant(card.dataset.variantId);
          return String(card.dataset.option2) === String(preferredPack) && variant?.available;
        });
        const preferred = matchingCards.find((card) => String(card.dataset.option2) === String(preferredPack));
        const firstAvailable = matchingCards.find((card) => this.getVariant(card.dataset.variantId)?.available);
        const target = preferredAvailable || preferred || firstAvailable || matchingCards[0];

        if (target) this.selectVariant(target.dataset.variantId);
      }

      setActiveSize(optionValue, focusSelection = false) {
        if (!this.splitOptions || optionValue == null) return;
        this.activeSize = String(optionValue);

        this.sizeButtons.forEach((button) => {
          const selected = String(button.dataset.optionValue) === this.activeSize;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-checked', selected ? 'true' : 'false');
          if (selected && focusSelection && this.sizeTrack) {
            const trackRect = this.sizeTrack.getBoundingClientRect();
            const buttonRect = button.getBoundingClientRect();
            if (buttonRect.left < trackRect.left || buttonRect.right > trackRect.right) {
              button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
          }
        });

        this.cards.forEach((card) => {
          const shell = card.closest('[data-ipvs-card-shell]');
          if (shell) shell.hidden = String(card.dataset.option1) !== this.activeSize;
        });

        requestAnimationFrame(() => {
          this.syncAttachedLabelOffsets();
          this.updateArrows();
        });
      }

      selectVariant(variantId) {
        const variant = this.getVariant(variantId);
        if (!variant) return;

        this.setSelected(variant.id, false);
        this.syncOtherPickers(variant);
        this.syncNativePicker(variant);
        this.updateProductForm(variant);

        if (this.dataset.updateUrl === 'true' && window.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant, sectionId: this.sectionId, source: 'impressiko-variant-cards' }
        }));
      }

      syncNativePicker(variant) {
        // Dawn replaces variant-selects after an option change, so always read the live node.
        this.nativePicker = this.querySelector('variant-selects.ipvs__native-picker');
        const nativeSelects = Array.from(this.nativePicker?.querySelectorAll('[data-ipvs-native-option]') || []);
        nativeSelects.forEach((select, index) => {
          if (variant.options[index] == null) return;
          select.value = variant.options[index];
          Array.from(select.options).forEach((option) => {
            option.toggleAttribute('selected', option.value === String(variant.options[index]));
          });
        });

        const lastSelect = nativeSelects[nativeSelects.length - 1];
        if (lastSelect) lastSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncOtherPickers(variant) {
        if (!this.productInfo) return;
        const pickers = this.productInfo.querySelectorAll('variant-selects, variant-radios');

        pickers.forEach((picker) => {
          if (picker === this.nativePicker || this.nativePicker?.contains(picker)) return;

          const groups = picker.querySelectorAll('.product-form__input, fieldset');
          groups.forEach((group, index) => {
            const optionValue = variant.options[index];
            if (optionValue == null) return;

            const select = group.querySelector('select');
            if (select) select.value = optionValue;

            group.querySelectorAll('input[type="radio"]').forEach((input) => {
              input.checked = String(input.value) === String(optionValue);
            });
          });
        });
      }

      updateProductForm(variant) {
        const forms = new Set();
        const mainForm = this.getProductForm();
        if (mainForm) forms.add(mainForm);
        this.productInfo?.querySelectorAll('form[action*="/cart/add"]').forEach((form) => forms.add(form));

        forms.forEach((form) => {
          const input = form.querySelector('[name="id"]');
          if (!input) return;
          input.value = variant.id;
          input.setAttribute('value', variant.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));

          const addButton = form.querySelector('[name="add"]');
          if (addButton) {
            addButton.disabled = !variant.available;
            addButton.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
          }
        });
      }

      handleDocumentChange(event) {
        const eventProductInfo = event.target?.closest?.('product-info');
        if (eventProductInfo && this.productInfo && eventProductInfo !== this.productInfo) return;

        if (
          event.target?.matches?.('[name="id"], select[name^="options"], input[name^="options"]') ||
          event.target?.closest?.('variant-selects, variant-radios')
        ) {
          this.scheduleExternalSync();
        }
      }

      handleVariantEvent(event) {
        const detail = event.detail || {};
        const detailSection = detail.sectionId || detail.section_id || detail.section;
        if (detailSection && String(detailSection) !== String(this.sectionId)) return;

        const id = detail.variant?.id || detail.productVariant?.id || detail.variantId || detail.variant_id;
        if (id) this.setSelected(id, detail.source !== 'impressiko-variant-cards');
        this.scheduleExternalSync();
      }

      scheduleExternalSync() {
        this.timers.forEach((timer) => clearTimeout(timer));
        this.timers = [0, 80, 240].map((delay) =>
          setTimeout(() => this.syncFromProductForm(), delay)
        );
      }

      syncFromProductForm() {
        const id = this.getProductForm()?.querySelector('[name="id"]')?.value;
        if (id && this.getVariant(id)) this.setSelected(id, true);
      }

      setSelected(variantId, allowScroll) {
        if (!variantId) return;
        this.currentVariantId = String(variantId);
        const currentVariant = this.getVariant(this.currentVariantId);
        if (this.splitOptions && currentVariant?.options?.[0] != null) {
          this.setActiveSize(currentVariant.options[0], allowScroll);
        }

        let selectedCard = null;
        this.cards.forEach((card) => {
          const selected = String(card.dataset.variantId) === this.currentVariantId;
          card.classList.toggle('is-selected', selected);
          card.setAttribute('aria-selected', selected ? 'true' : 'false');
          card.closest('[data-ipvs-card-shell]')?.classList.toggle('is-selected', selected);
          if (selected) selectedCard = card;
        });

        if (allowScroll && this.dataset.autoScroll === 'true' && selectedCard) {
          this.scrollCardIntoView(selectedCard);
        }
      }

      scrollCardIntoView(card) {
        if (!this.track) return;
        const scrollTarget = card.closest('[data-ipvs-card-shell]') || card;
        const trackRect = this.track.getBoundingClientRect();
        const cardRect = scrollTarget.getBoundingClientRect();
        const outsideTrack = this.dataset.layout === 'vertical_list'
          ? cardRect.top < 0 || cardRect.bottom > window.innerHeight
          : cardRect.left < trackRect.left || cardRect.right > trackRect.right;
        if (outsideTrack) {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      }

      scrollTrack(direction) {
        const card = this.cards.find((candidate) => !candidate.closest('[data-ipvs-card-shell]')?.hidden);
        const cardShell = card?.closest('[data-ipvs-card-shell]') || card;
        if (!this.track || !cardShell) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
        this.track.scrollBy({ left: direction * (cardShell.getBoundingClientRect().width + gap), behavior: 'smooth' });
      }

      updateArrows() {
        if (!this.track || this.dataset.layout === 'vertical_list') return;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      }

      getProductForm() {
        return document.getElementById(this.formId) ||
          this.productInfo?.querySelector('form[action*="/cart/add"]');
      }

      getVariant(id) {
        return this.variants.find((variant) => String(variant.id) === String(id));
      }
    }

    customElements.define('impressiko-variant-cards', ImpressikoVariantCards);
  })();

/* impressiko-buy-buttons.liquid */
if (!customElements.get('impressiko-buy-buttons')) {
    customElements.define('impressiko-buy-buttons', class ImpressikoBuyButtons extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.sectionId = this.dataset.sectionId;
        this.mainCard = this.querySelector('[data-ib-main-card]');
        this.form = this.querySelector('form[action*="/cart/add"]');
        this.variantInput = this.querySelector('[data-ib-variant-input]');
        this.mainAdd = this.querySelector('[data-ib-main-add]');
        this.mainBuy = this.querySelector('[data-ib-main-buy]');
        this.sticky = this.querySelector('[data-ib-sticky]');
        this.stickyAdd = this.querySelector('[data-ib-sticky-add]');
        this.stickyBuy = this.querySelector('[data-ib-sticky-buy]');
        this.errorWrapper = this.querySelector('[data-ib-error-wrapper]');
        this.errorMessage = this.querySelector('[data-ib-error-message]');
        this.variants = this.readJSON('[data-ib-variants]', []);
        this.variantMap = new Map(this.variants.map((variant) => [String(variant.id), variant]));
        this.mountSticky();
        this.handleStickyAdd = () => this.addFromSticky();
        this.handleBuyNow = () => this.buyNow();
        this.handleUpdate = () => window.requestAnimationFrame(() => {
          this.syncVariantState();
          this.evaluateSticky();
        });
        this.handleDocumentClick = () => window.setTimeout(() => this.evaluateSticky(), 0);
        this.handleKeyUp = (event) => { if (event.key === 'Escape') window.setTimeout(() => this.evaluateSticky(), 0); };

        this.stickyAdd?.addEventListener('click', this.handleStickyAdd);
        this.mainBuy?.addEventListener('click', this.handleBuyNow);
        this.stickyBuy?.addEventListener('click', this.handleBuyNow);
        this.form?.addEventListener('change', this.handleUpdate);
        this.form?.addEventListener('input', this.handleUpdate);
        window.addEventListener('scroll', this.handleUpdate, { passive: true });
        window.addEventListener('resize', this.handleUpdate, { passive: true });
        document.addEventListener('click', this.handleDocumentClick, true);
        document.addEventListener('keyup', this.handleKeyUp);
        ['variant:change', 'variantChange', 'product:variant-change'].forEach((eventName) => document.addEventListener(eventName, this.handleUpdate));
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data?.sectionId || String(data.sectionId) === String(this.sectionId)) this.handleUpdate();
          });
        }

        this.observer = new MutationObserver(() => this.handleUpdate());
        this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        document.querySelectorAll('cart-drawer, header-drawer, menu-drawer').forEach((drawer) => {
          this.observer.observe(drawer, {
            subtree: drawer.tagName !== 'CART-DRAWER',
            attributes: true,
            attributeFilter: ['class', 'open', 'aria-hidden', 'aria-expanded']
          });
        });
        this.syncVariantState();
        this.evaluateSticky();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.stickyAdd?.removeEventListener('click', this.handleStickyAdd);
        this.mainBuy?.removeEventListener('click', this.handleBuyNow);
        this.stickyBuy?.removeEventListener('click', this.handleBuyNow);
        this.form?.removeEventListener('change', this.handleUpdate);
        this.form?.removeEventListener('input', this.handleUpdate);
        window.removeEventListener('scroll', this.handleUpdate);
        window.removeEventListener('resize', this.handleUpdate);
        document.removeEventListener('click', this.handleDocumentClick, true);
        document.removeEventListener('keyup', this.handleKeyUp);
        ['variant:change', 'variantChange', 'product:variant-change'].forEach((eventName) => document.removeEventListener(eventName, this.handleUpdate));
        this.unsubscribeVariantChange?.();
        this.observer?.disconnect();
        this.unmountSticky();
        this.initialized = false;
      }

      unmountSticky() {
        this.portalStyleObserver?.disconnect();
        if (this.portal) {
          // A section can be removed and reinserted by the Theme Editor. Keep its node for reconnect.
          if (this.sticky?.parentNode === this.portal) this.appendChild(this.sticky);
          this.portal.remove();
          this.portal = null;
        }
        this.portalStyleObserver = null;
      }

      mountSticky() {
        if (!this.sticky || !document.body) return;
        const portalId = `ImpressikoStickyPortal-${this.sectionId}-${this.dataset.blockId}`;
        document.getElementById(portalId)?.remove();
        this.portal = document.createElement('div');
        this.portal.id = portalId;
        this.syncPortalStyles();
        document.body.appendChild(this.portal);
        this.portal.appendChild(this.sticky);
        this.portalStyleObserver = new MutationObserver(() => this.syncPortalStyles());
        this.portalStyleObserver.observe(this, { attributes: true, attributeFilter: ['style'] });
      }

      syncPortalStyles() {
        if (this.portal) this.portal.style.cssText = this.style.cssText;
      }

      readJSON(selector, fallback) {
        try { return JSON.parse(this.querySelector(selector)?.textContent || ''); }
        catch (_error) { return fallback; }
      }

      rootRoute() {
        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }

      drawerIsOpen() {
        const body = document.body;
        if (['overflow-hidden', 'menu-opening', 'js-drawer-open', 'cart-drawer-open'].some((name) => body.classList.contains(name))) return true;
        return Boolean(document.querySelector([
          'cart-drawer.active',
          'cart-drawer[open]',
          '#CartDrawer.active',
          '.cart-drawer.active',
          '.drawer.active',
          '.drawer[open]',
          'header-drawer details[open]',
          'menu-drawer details[open]',
          'details[open] > #menu-drawer',
          '[data-cart-drawer][aria-hidden="false"]'
        ].join(',')));
      }

      evaluateSticky() {
        if (!this.sticky || !this.mainCard) return;
        const drawerOpen = this.drawerIsOpen();
        const afterScroll = this.dataset.stickyDisplay === 'after_scroll';
        const passedMainButtons = this.mainCard.getBoundingClientRect().bottom < 0;
        const show = !drawerOpen && (!afterScroll || passedMainButtons);
        this.sticky.classList.toggle('is-drawer-hidden', drawerOpen);
        this.sticky.classList.toggle('is-visible', show);
        const ariaHidden = show ? 'false' : 'true';
        if (this.sticky.getAttribute('aria-hidden') !== ariaHidden) this.sticky.setAttribute('aria-hidden', ariaHidden);
      }

      currentVariant() {
        return this.variantMap.get(String(this.variantInput?.value || ''));
      }

      syncVariantState() {
        const variant = this.currentVariant();
        const available = Boolean(variant?.available);
        const soldOutLabel = this.dataset.soldOutLabel || 'SOLD OUT';
        const addLabel = this.dataset.addLabel || 'ADD TO CART';
        [this.mainAdd, this.mainBuy, this.stickyAdd, this.stickyBuy].forEach((button) => {
          if (!button) return;
          button.disabled = !available;
          button.setAttribute('aria-disabled', available ? 'false' : 'true');
        });
        const mainText = this.mainAdd?.querySelector('[data-ib-add-text]');
        const stickyText = this.stickyAdd?.querySelector('[data-ib-sticky-add-text]');
        if (mainText && !this.mainAdd.classList.contains('is-loading')) mainText.textContent = available ? addLabel : soldOutLabel;
        if (stickyText) stickyText.textContent = available ? addLabel : soldOutLabel;
      }

      addFromSticky() {
        const variant = this.currentVariant();
        if (!this.form || !variant?.available) return;
        this.sticky?.classList.remove('is-visible');
        if (this.mainAdd && !this.mainAdd.disabled) this.mainAdd.click();
        else this.form.requestSubmit();
      }

      showError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = message || '';
        this.errorWrapper.hidden = !message;
      }

      setBuyLoading(loading) {
        [this.mainBuy, this.stickyBuy].forEach((button) => {
          if (!button) return;
          button.classList.toggle('is-loading', loading);
          button.setAttribute('aria-busy', loading ? 'true' : 'false');
          button.disabled = loading;
        });
      }

      async buyNow() {
        const variant = this.currentVariant();
        if (!this.form || !variant?.available || this.buying) return;
        this.buying = true;
        this.setBuyLoading(true);
        this.showError('');
        this.sticky?.classList.remove('is-visible');
        try {
          const formData = new FormData(this.form);
          if (!formData.get('quantity')) formData.set('quantity', '1');
          const response = await fetch(`${this.rootRoute()}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added to the cart.');
          window.location.assign(`${this.rootRoute()}checkout`);
        } catch (error) {
          this.showError(error.message || 'Something went wrong. Please try again.');
          this.buying = false;
          this.setBuyLoading(false);
          this.syncVariantState();
          this.evaluateSticky();
        }
      }
    });
  }
